// RBAC Phase 4 passkey hardening: the "Issue passkey enrolment code" action on the user detail page.
// The code (`pket_…`) is shown once; admin-api does not keep it.

/** Shown only for someone else's active account, and only with iam.users.write. */
export function canIssuePasskeyEnrolmentCode(input: {
  canWriteUsers: boolean;
  viewerId: string | null | undefined;
  user: { id: string; isActive: boolean } | null | undefined;
}): boolean {
  return input.canWriteUsers && !!input.user && input.user.isActive && !!input.viewerId && input.viewerId !== input.user.id;
}

/**
 * A sign-in link that carries the code: `<origin><loginPath>?enrolment=<code>`. The login page keeps
 * the code for the passkey step, so the user does not have to type it.
 */
export function passkeyEnrolmentLink(origin: string, loginPath: string, code: string): string {
  const path = loginPath.startsWith('/') ? loginPath : `/${loginPath}`;
  return `${origin.replace(/\/$/, '')}${path}?enrolment=${encodeURIComponent(code)}`;
}

/** "expires in 23 hours"-style copy; `expired` once past. */
export function describeEnrolmentExpiry(expiresAt: string, now: Date = new Date()): { expired: boolean; relative: string } {
  const at = new Date(expiresAt).getTime();
  if (!Number.isFinite(at)) return { expired: false, relative: 'expiry unknown' };
  const ms = at - now.getTime();
  if (ms <= 0) return { expired: true, relative: 'expired' };
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return { expired: false, relative: 'expires in under a minute' };
  if (minutes < 90) return { expired: false, relative: `expires in ${minutes} minute${minutes === 1 ? '' : 's'}` };
  const hours = Math.round(minutes / 60);
  if (hours < 48) return { expired: false, relative: `expires in ${hours} hours` };
  const days = Math.round(hours / 24);
  return { expired: false, relative: `expires in ${days} days` };
}

/** Error copy for the issue call (admin-api codes); MFA_REQUIRED is handled by the step-up interceptor. */
export function enrolmentIssueErrorText(err: unknown): string {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  const body = (data && typeof data === 'object' ? data : {}) as { code?: unknown; error?: unknown };
  switch (body.code) {
    case 'NOT_LINKED':
      return 'This user has not signed in through the authentication service yet, so there is no account to enrol. Ask them to sign in once first.';
    case 'ACCOUNT_INACTIVE':
    case 'ACCOUNT_DISABLED':
      return 'This account is deactivated. Reactivate it before issuing an enrolment code.';
    case 'SELF_ENROLMENT':
      return 'You cannot issue an enrolment code for yourself. Ask another IAM admin.';
    default:
      return typeof body.error === 'string' && body.error ? body.error : 'Could not issue a passkey enrolment code.';
  }
}
